import type { AxiosProgressEvent } from 'axios';

import { axiosInstance } from '@/api/axiosInstance';
import { uploadProgressPercent } from '@/api/upload';
import type { ScanStatus } from '@/entities/applications/scan';

const imageUploadBase = '/applications/v1/image-uploads/';

export type ImageUploadKind = 'logo' | 'preview';

export type ImageUploadStatus = 'ready' | 'used' | 'expired';

export type ImageUploadApplication = {
  uuid: string;
  name: string;
  slug: string;
};

export type ImageUploadImage = {
  uuid: string | null;
  url: string;
  scan_status?: ScanStatus;
};

export type ImageUploadLink = {
  application: ImageUploadApplication;
  kind: ImageUploadKind;
  filename: string | null;
  expires_at: string;
  status: ImageUploadStatus;
  remaining_preview_slots: number;
  image?: ImageUploadImage | null;
};

export type ImageUploadResult = {
  application: ImageUploadApplication;
  kind: ImageUploadKind;
  image: ImageUploadImage;
};

const imageUploadUrl = (token: string) =>
  `${imageUploadBase}${encodeURIComponent(token)}/`;

export const getImageUpload = async (
  token: string,
  { signal }: { signal?: AbortSignal } = {},
) => {
  const response = await axiosInstance.get<ImageUploadLink>(
    imageUploadUrl(token),
    { signal },
  );
  return response.data;
};

export const submitImageUpload = async (
  token: string,
  file: File,
  {
    onProgress,
  }: { onProgress?: (percent: number, event: AxiosProgressEvent) => void } = {},
) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axiosInstance.post<ImageUploadResult>(
    imageUploadUrl(token),
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      onUploadProgress: onProgress
        ? (event) => onProgress(uploadProgressPercent(event), event)
        : undefined,
    },
  );
  return response.data;
};
